const BASE = import.meta.env.VITE_ATLAS_API_URL || 'https://atlas.nebulahq.work';

export type Agent = {
  id: string;
  name: string;
  agent_type: string;
  model: string;
  tools: string[];
  status: string;
  tasks_completed: number;
  max_iterations: number;
  system_prompt: string | null;
  created_at: string;
};

export type Task = {
  id: string;
  agent_id: string;
  prompt: string;
  status: string;
  priority: number;
  result: string | null;
  output: string;
  steps: number;
  created_at: string;
  completed_at: string | null;
};

export type Plan = {
  id: string;
  goal: string;
  model: string;
  steps: { step: number; action: string; agent: string }[];
  status: string;
  created_at: string;
};

async function req<T>(url: string, opts?: RequestInit): Promise<T> {
  const r = await fetch(`${BASE}${url}`, {
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });
  if (!r.ok) {
    const text = await r.text();
    throw new Error(`${r.status} ${r.statusText}: ${text.slice(0, 200)}`);
  }
  return r.json();
}

export const api = {
  getAgents: () => req<Agent[]>('/api/agents'),
  getAgent: (id: string) => req<Agent>(`/api/agents/${id}`),
  createAgent: (data: {
    name: string;
    type: string;
    model?: string;
    tools?: string[];
    system_prompt?: string;
  }) => req<Agent>('/api/agents', { method: 'POST', body: JSON.stringify(data) }),
  deleteAgent: (id: string) => req<{ ok: boolean }>(`/api/agents/${id}`, { method: 'DELETE' }),

  getTasks: (agent_id?: string, status?: string) => {
    const p = new URLSearchParams();
    if (agent_id) p.set('agent_id', agent_id);
    if (status) p.set('status', status);
    return req<Task[]>(`/api/tasks?${p}`);
  },
  getTask: (id: string) => req<Task>(`/api/tasks/${id}`),
  createTask: (data: { agent_id: string; prompt: string; priority?: number }) =>
    req<Task>('/api/tasks', { method: 'POST', body: JSON.stringify(data) }),
  cancelTask: (id: string) => req<{ ok: boolean }>(`/api/tasks/${id}/cancel`, { method: 'POST' }),

  execute: (data: { prompt: string; model?: string; system_prompt?: string }) =>
    req<{ id: string; result: string; status: string }>('/api/execute', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  plan: (data: { goal: string; model?: string }) =>
    req<Plan>('/api/plan', { method: 'POST', body: JSON.stringify(data) }),

  memory: {
    get: (type?: string) =>
      req<{ short_term: any[]; long_term: any[] }>(`/api/memory${type ? `?type=${type}` : ''}`),
    store: (key: string, value: string, type?: string) =>
      req<{ ok: boolean; stored: string }>('/api/memory', {
        method: 'POST',
        body: JSON.stringify({ key, value, type: type || 'short_term' }),
      }),
    clear: (type?: string) =>
      req<{ ok: boolean }>(`/api/memory${type ? `?type=${type}` : ''}`, { method: 'DELETE' }),
  },

  health: () => req<{ status: string; agents: number; tasks: number }>('/health'),

  wsTaskUrl: (taskId: string) => {
    const wsBase = BASE.replace('http://', 'ws://').replace('https://', 'wss://');
    return `${wsBase}/ws/tasks/${taskId}`;
  },
};
